// Chlorine / pH indicator levels, matching the mobile app's Water Care screen.
// The firmware reports sbORPind and sbpHind as 0–4, low to high, with 2 as the
// target. It does not report the boundaries, so these are the published ranges.

export type IndicatorTone = 'good' | 'caution' | 'bad';

/** Chlorine (ppm FCL), indexed by sbORPind. */
export const CHLORINE_RANGES = ['< 0.1', '0.1 - 0.5', '0.6 - 1.5', '1.6 - 3.0', '> 3.0'];

/** pH, indexed by sbpHind. */
export const PH_RANGES = ['< 6.8', '6.8 - 7.2', '7.3 - 7.8', '7.9 - 8.2', '> 8.2'];

/**
 * `{ word, tone }` for an indicator value. 1 and 3 ("caution low/high") get an
 * amber tone so a slight drift doesn't look as alarming as out-of-range.
 * Unknown or missing values return null so the caller can show a placeholder
 * rather than claim a level nobody measured.
 */
export function indicatorState(value: unknown): { word: string; tone: IndicatorTone } | null {
    switch (value) {
        case 0: return { word: 'LO', tone: 'bad' };
        case 1: return { word: 'LO', tone: 'caution' };
        case 2: return { word: 'OK', tone: 'good' };
        case 3: return { word: 'HI', tone: 'caution' };
        case 4: return { word: 'HI', tone: 'bad' };
        default: return null;
    }
}

// ── Spa usage (ORP setpoint) ────────────────────────────────────────────────
// People glyph counts are the mobile design's shorthand for bather load.
// `base` is the value the SpaBoy page stores; the page maps it to SBORPlo/hi.

export const USAGE_LEVELS = [
    { base: 0, label: 'LOW', people: 2, plus: false },
    { base: 50, label: 'MED', people: 4, plus: false },
    { base: 100, label: 'HIGH', people: 4, plus: true },
];

// ── Cell life ───────────────────────────────────────────────────────────────

export const CELL_LIFE_SEGMENTS = 5;

/** sbWear value the spa sends while it is still determining wear. */
export const CELL_LIFE_MEASURING = 101;

/**
 * Segments + tone for the cell-life meter. sbWear is remaining life (higher is
 * better). Ceil so any remaining life shows at least one bar; amber under 40%,
 * red under 20%. Missing sbWear reads as a full cell, as on mobile. 101 means
 * the spa is still measuring, so it gets no level or tone at all.
 */
export function cellLifeSegments(sbWear: unknown): { filled: number; tone: IndicatorTone | 'measuring' } {
    if (Number(sbWear) === CELL_LIFE_MEASURING) return { filled: 0, tone: 'measuring' };
    const pct = sbWear == null ? 100 : Math.max(0, Math.min(100, Number(sbWear) || 0));
    const filled = Math.min(CELL_LIFE_SEGMENTS, Math.ceil((pct / 100) * CELL_LIFE_SEGMENTS));
    const tone: IndicatorTone = pct < 20 ? 'bad' : pct < 40 ? 'caution' : 'good';
    return { filled, tone };
}

// ── Sanitation ──────────────────────────────────────────────────────────────
// OzCy is a mode (what the ozone generator runs alongside), not a quantity.

export const OZONE_CYCLE_LABELS = ['OFF', 'P1 LOW', 'P1 HIGH', 'YESS'];

export const ozoneCycleLabel = (value: unknown) => OZONE_CYCLE_LABELS[value as number] ?? String(value ?? '');
